import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon,
  AttachFile as AttachFileIcon,
  DragIndicator as DragIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { format, isPast } from 'date-fns';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../../supabaseClient';

const priorityColors = {
  low: '#4caf50',
  medium: '#ff9800',
  high: '#f44336',
  urgent: '#9c27b0'
};

const StyledTaskCard = styled(Card)(({ theme, priority, completed, isdragging }) => ({
  marginBottom: theme.spacing(2),
  cursor: isdragging ? 'grabbing' : 'pointer',
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
  background: completed 
    ? `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.4)})`
    : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
  boxShadow: isdragging 
    ? `0 12px 32px ${alpha(theme.palette.grey[900], 0.3)}`
    : `0 4px 12px ${alpha(theme.palette.grey[400], 0.15)}`,
  position: 'relative',
  overflow: 'hidden',
  opacity: completed ? 0.7 : isdragging ? 0.9 : 1,
  contain: 'layout style paint',
  transition: isdragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: isdragging ? 9999 : 'auto',
  minHeight: '160px !important',
  maxHeight: '160px !important',
  height: '160px !important',
  width: '100%',
  '&:hover': !isdragging ? {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.grey[400], 0.25)}`,
    border: `1px solid ${alpha(priorityColors[priority], 0.4)}`,
    '&::before': {
      opacity: 1
    }
  } : {},
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: priorityColors[priority],
    opacity: 0.8,
    transition: 'opacity 0.3s ease'
  }
}));

// Profile Avatar Component
const ProfileAvatar = ({ userId, user, size = 28, sx = {} }) => {
  const [avatarUrl, setAvatarUrl] = useState('');
  
  useEffect(() => {
    if (!userId) return;
    
    const loadAvatar = async () => {
      try {
        const { data: files, error } = await supabase.storage
          .from('profile-avatars')
          .list(`${userId}/`, {
            limit: 1,
            sortBy: { column: 'created_at', order: 'desc' }
          });

        if (error || !files || files.length === 0) return;

        const fileName = `${userId}/${files[0].name}`;
        const { data, error: signedUrlError } = await supabase.storage
          .from('profile-avatars')
          .createSignedUrl(fileName, 3600);
        
        if (!signedUrlError && data?.signedUrl) {
          setAvatarUrl(data.signedUrl);
        }
      } catch (err) {
        // Silent fail
      }
    };

    loadAvatar();
  }, [userId]);

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <Avatar
      src={avatarUrl}
      sx={{ 
        width: size, 
        height: size, 
        fontSize: `${size * 0.4}px`,
        backgroundColor: 'grey.400',
        color: 'white',
        fontWeight: 500,
        ...sx
      }}
    >
      {getInitials()}
    </Avatar>
  );
};

const TaskCard = ({ task, onEdit, onDelete, onComplete, onAssign, users, isDragging = false }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: sortableIsDragging,
  } = useSortable({ 
    id: task.id,
    disabled: false
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCardClick = (event) => {
    // Don't trigger edit if clicking on drag handle or menu button
    if (event.target.closest('.drag-handle') || event.target.closest('.menu-button')) {
      return;
    }
    onEdit(task);
  };

  const assignedUser = users?.[0]; // Always current user since tasks are personal
  const isTaskOverdue = task.due_date && isPast(new Date(task.due_date));
  const isCurrentlyDragging = isDragging || sortableIsDragging;

  return (
    <StyledTaskCard
      ref={setNodeRef}
      style={style}
      priority={task.priority}
      completed={task.status === 'completed'}
      isdragging={isCurrentlyDragging}
      onClick={handleCardClick}
    >
      <CardContent sx={{ 
        p: 2.5, 
        '&:last-child': { pb: '20px !important' },
        height: 'calc(160px - 0px) !important',
        minHeight: '160px !important',
        maxHeight: '160px !important',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxSizing: 'border-box'
      }}>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, mr: 1 }}>
              <Box
                className="drag-handle"
                {...attributes}
                {...listeners}
                sx={{
                  cursor: 'grab',
                  display: 'flex',
                  alignItems: 'center',
                  mr: 1.5,
                  mt: 0.25,
                  padding: 0.5,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.grey[400], 0.1),
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                  '&:hover': {
                    backgroundColor: alpha(priorityColors[task.priority], 0.1),
                    borderColor: priorityColors[task.priority]
                  },
                  '&:active': {
                    cursor: 'grabbing',
                    transform: 'scale(0.95)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <DragIcon 
                  fontSize="small" 
                  sx={{ 
                    color: theme.palette.grey[500],
                    fontSize: '1rem'
                  }} 
                />
              </Box>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 500, 
                fontSize: '0.95rem',
                color: 'text.primary',
                lineHeight: 1.4,
                flex: 1
              }}>
                {task.title}
              </Typography>
            </Box>
            <IconButton 
              className="menu-button"
              size="small" 
              onClick={handleMenuClick}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '&:hover': {
                  backgroundColor: alpha(priorityColors[task.priority], 0.1),
                  borderColor: priorityColors[task.priority]
                },
                '&:active': {
                  transform: 'scale(0.95)'
                },
                transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.1s ease'
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip
              icon={<FlagIcon />}
              label={task.priority}
              size="small"
              sx={{
                backgroundColor: priorityColors[task.priority],
                color: 'white',
                fontWeight: 500,
                borderRadius: 2,
                fontSize: '0.7rem',
                height: 24,
                '& .MuiChip-icon': { 
                  color: 'white',
                  fontSize: '0.875rem'
                },
                boxShadow: `0 2px 8px ${alpha(priorityColors[task.priority], 0.3)}`
              }}
            />
            
            {task.collaborators?.length > 0 && (
              <Chip
                icon={<GroupIcon />}
                label={`${task.collaborators.length}`}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  fontWeight: 500,
                  borderRadius: 2,
                  fontSize: '0.7rem',
                  height: 24,
                  '& .MuiChip-icon': { 
                    color: theme.palette.success.main,
                    fontSize: '0.875rem'
                  }
                }}
              />
            )}
            
            {task.due_date && (
              <Chip
                icon={<CalendarIcon />}
                label={format(new Date(task.due_date), 'MMM dd')}
                size="small"
                sx={{
                  backgroundColor: isTaskOverdue ? theme.palette.error.main : alpha(theme.palette.info.main, 0.1),
                  color: isTaskOverdue ? 'white' : theme.palette.info.main,
                  border: `1px solid ${isTaskOverdue ? theme.palette.error.main : alpha(theme.palette.info.main, 0.3)}`,
                  fontWeight: 500,
                  borderRadius: 2,
                  fontSize: '0.7rem',
                  height: 24,
                  '& .MuiChip-icon': { 
                    color: isTaskOverdue ? 'white' : theme.palette.info.main,
                    fontSize: '0.875rem'
                  }
                }}
              />
            )}

            {task.attachment_urls?.length > 0 && (
              <Chip
                icon={<AttachFileIcon />}
                label={task.attachment_urls.length}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                  fontWeight: 500,
                  borderRadius: 2,
                  fontSize: '0.7rem',
                  height: 24,
                  '& .MuiChip-icon': { 
                    color: theme.palette.secondary.main,
                    fontSize: '0.875rem'
                  }
                }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Show task owner/creator */}
            {(() => {
              const owner = users?.find(u => u.id === task.created_by);
              if (owner) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={owner.full_name || owner.email || 'Owner'}>
                      <ProfileAvatar
                        userId={owner.id}
                        user={owner}
                        size={28}
                        sx={{
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          backgroundColor: 'grey.400',
                          color: 'white'
                        }}
                      />
                    </Tooltip>
                  </Box>
                );
              }
              return null;
            })()}
            
            {/* Show ALL collaborators */}
            {task.collaborators?.map((collab, index) => {
              const collaboratorUser = users?.find(u => u.id === collab.user_id);
              if (collaboratorUser) {
                return (
                  <Box key={collab.id || index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={collaboratorUser.full_name || collaboratorUser.email || 'Collaborator'}>
                      <ProfileAvatar
                        userId={collaboratorUser.id}
                        user={collaboratorUser}
                        size={28}
                        sx={{
                          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          backgroundColor: 'grey.400',
                          color: 'white'
                        }}
                      />
                    </Tooltip>
                  </Box>
                );
              }
              return null;
            })}
          </Box>

          {task.tags?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {task.tags.slice(0, 2).map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }}
                />
              ))}
              {task.tags.length > 2 && (
                <Chip
                  label={`+${task.tags.length - 2}`}
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.grey[400], 0.1),
                    color: theme.palette.grey[600],
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        disableScrollLock={true}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            position: 'absolute !important',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
            zIndex: 9999,
            minWidth: 120,
            contain: 'layout style paint',
            '& .MuiMenuItem-root': {
              borderRadius: 2,
              mx: 1,
              my: 0.5,
              fontWeight: 500,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08)
              }
            }
          }
        }}
        sx={{
          zIndex: 9999
        }}
      >
        <MenuItem onClick={() => { onEdit(task); handleMenuClose(); }}>
          Edit
        </MenuItem>
        {task.status !== 'completed' && (
          <MenuItem onClick={() => { onComplete(task.id); handleMenuClose(); }}>
            Mark Complete
          </MenuItem>
        )}
        <MenuItem 
          onClick={() => { onDelete(task.id); handleMenuClose(); }} 
          sx={{ 
            color: 'error.main',
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.08)
            }
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </StyledTaskCard>
  );
};

export default TaskCard;
